/**
 * A round paced by the player's Spin (decision 55): a title's presentation awaits `awaitSpin()` inside its round
 * (slot-coin-hold before each bonus respin, the round's outcome the server's already), the bar turns its Spin button to
 * the spin face while the round waits, and the player's press — the button, or the spacebar; never autoplay, which only
 * requests rounds — lets the round go on. The press sends nothing: it paces a round that is decided.
 */
export interface PlayerSpinPort {
  /** Waits for the player's Spin; passes at once while nobody can press it (a history replay, a restore). */
  awaitSpin(): Promise<void>;
}

/**
 * The shell's side of `PlayerSpinPort`: whether a round waits (the bar renders it), the press that ends the wait, and
 * the passing that lets every wait through while no player can press Spin — a history replay, which has no Spin button,
 * and a restore, which plays under the boot cover.
 */
export class PlayerSpinGate implements PlayerSpinPort {
  private waits: Array<() => void> = [];
  private passing = false;
  private readonly listeners = new Set<() => void>();

  public awaitSpin(): Promise<void> {
    if (this.passing) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.waits.push(resolve);
      if (this.waits.length === 1) this.emit();
    });
  }

  /** A round waits for the player's Spin. */
  public get waiting(): boolean {
    return this.waits.length > 0;
  }

  /** The player's Spin: the wait ends (true), or nothing waited (false: the press is the button's usual one). */
  public press(): boolean {
    if (this.waits.length === 0) return false;
    const waits = this.waits;
    this.waits = [];
    this.emit();
    for (const resolve of waits) resolve();
    return true;
  }

  /** While nobody can press Spin, every wait passes at once, one already waiting too. */
  public setPassing(passing: boolean): void {
    this.passing = passing;
    if (passing) this.press();
  }

  /** Every change of `waiting`. */
  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of [...this.listeners]) listener();
  }
}
